import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import type { SkillSyncStatus } from '~/shared/data-contract/skill';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/ui/core/dropdown-menu/DropdownMenu';

import { useSkillCatalogOptions } from '../../job-architecture-v2/api/use-skill-catalog-options';
import { useSkillCatalogRefetch } from '../../job-architecture-v2/api/use-skill-catalog-refetch';
import { useUpdateSkillCatalogEntry } from '../../job-architecture-v2/api/use-update-skill-catalog-entry';
import { SyncStatusBadge, SyncStatusOptions } from '../../job-architecture-v2/skills/sync-status-options';
import { useCachedSkillSyncStatus } from './api/use-cached-skill-sync-status';

export const SkillSyncStatusAction: FunctionComponent<{ readonly skillId: string }> = ({ skillId }) => {
    const { data: catalogOptions } = useSkillCatalogOptions();
    const currentStatus = useCachedSkillSyncStatus(skillId);
    const scheduleCatalogRefetch = useSkillCatalogRefetch();
    const { mutate: updateSkillCatalogEntry } = useUpdateSkillCatalogEntry(scheduleCatalogRefetch);

    const handleStatusChange = useCallback(
        (syncStatus: SkillSyncStatus): void => {
            if (syncStatus === currentStatus) return;

            updateSkillCatalogEntry({ skillId, syncStatus });
        },
        [currentStatus, skillId, updateSkillCatalogEntry],
    );

    if (catalogOptions?.canManageSkillCatalog !== true || is.undefined(currentStatus)) {
        return null;
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger>
                <SyncStatusBadge
                    currentStatus={currentStatus}
                    data-testid="skill-details-sync-status"
                    interactive={true}
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={5} style={{ maxWidth: '250px' }}>
                <SyncStatusOptions currentStatus={currentStatus} onStatusChange={handleStatusChange} />
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
